import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "../api/axios";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import CommonTable from "../components/CommonTable";
import { PreferencesContext } from "../context/PreferencesContext";
import { useMessage } from "../context/MessageContext";
import { useConfirm } from "../context/ConfirmContext";
import ItemDialog from "../components/ItemDialog";
import CollectionDialog from "../components/CollectionDialog";

const defaultForm = {
  name: "",
  description: "",
  price: "",
  productLine: "book",
  grades: [],
  programs: [],
  subjects: [],
  category: [],
  costMode: "costPrice",
  costPrice: "",
  commissionPercent: "",
};

const colDefaultForm = {
  ...defaultForm,
  items: [],
};

const BookCatalog = () => {
  const { organization, academicYear } = useContext(PreferencesContext);
  const { showMessage } = useMessage();
  const { confirm } = useConfirm();

  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [masters, setMasters] = useState({
    grades: [],
    programs: [],
    subjects: [],
  });

  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemForm, setItemForm] = useState(defaultForm);
  const [editingItem, setEditingItem] = useState(null);

  const [colDialogOpen, setColDialogOpen] = useState(false);
  const [colForm, setColForm] = useState(colDefaultForm);
  const [editingCol, setEditingCol] = useState(null);

  const [catInput, setCatInput] = useState("");

  useEffect(() => {
    if (organization && academicYear) {
      fetchVendors();
      fetchMasters();
    }
  }, [organization, academicYear]);

  useEffect(() => {
    if (selectedVendor) fetchCatalog();
  }, [selectedVendor]);

  const fetchVendors = async () => {
    try {
      const res = await axios.get("/vendors", {
        params: {
          orgId: organization,
          academicYear,
          vendorType: "book",
        },
      });
      const list = res.data || [];
      setVendors(list);
      setSelectedVendor(list[0]?._id || "");
    } catch {
      showMessage("Failed to load book vendors", "error");
    }
  };

  const fetchMasters = async () => {
    try {
      const res = await axios.get(`/masters/${organization}`);
      setMasters(res.data);
    } catch {
      showMessage("Failed to load masters", "error");
    }
  };

  const fetchCatalog = async () => {
    try {
      const res = await axios.get("/catalogs", {
        params: { vendorId: selectedVendor, academicYear },
      });
      setCatalog(res.data || { categories: [], items: [], collections: [] });
    } catch {
      showMessage("Failed to load catalog", "error");
    }
  };

  const bookItems = useMemo(
    () =>
      (catalog?.items || []).filter(
        (item) => (item.productLine || "book") !== "uniform"
      ),
    [catalog]
  );

  const saveCatalogLinks = async (updates) => {
    try {
      const res = await axios.post("/catalogs", {
        vendor: selectedVendor,
        academicYear,
        organization,
        categories: updates.categories,
        items: updates.items.map((i) => i._id),
        collections: updates.collections.map((c) => c._id),
      });
      setCatalog(res.data);
      showMessage("Catalog updated", "success");
      return res.data;
    } catch {
      showMessage("Failed to update catalog", "error");
      throw new Error("Catalog update failed");
    }
  };

  const handleAddCategory = async () => {
    if (!catInput.trim()) return;
    const categories = [...(catalog.categories || []), catInput.trim()];
    setCatInput("");
    const updatedCatalog = await saveCatalogLinks({ ...catalog, categories });
    setCatalog(updatedCatalog);
  };

  const handleDeleteCategory = async (cat) => {
    const categories = (catalog.categories || []).filter((c) => c !== cat);
    const updatedCatalog = await saveCatalogLinks({ ...catalog, categories });
    setCatalog(updatedCatalog);
  };

  const ensureCatalog = async () => {
    if (catalog?._id) return catalog;
    const res = await axios.post("/catalogs", {
      vendor: selectedVendor,
      academicYear,
      organization,
      categories: [],
      items: [],
      collections: [],
    });
    setCatalog(res.data);
    return res.data;
  };

  const openItemDialog = (item = null) => {
    if (item) {
      setItemForm({
        ...item,
        productLine: "book",
        costMode:
          item.costPrice != null ? "costPrice" : "commissionPercent",
      });
      setEditingItem(item);
    } else {
      setItemForm(defaultForm);
      setEditingItem(null);
    }
    setItemDialogOpen(true);
  };

  const handleItemSave = async () => {
    if (!itemForm.name.trim())
      return showMessage("Item name required", "warning");
    if (!itemForm.price || itemForm.price <= 0)
      return showMessage("Valid price required", "warning");

    try {
      const cat = catalog?._id ? catalog : await ensureCatalog();
      const payload = {
        ...itemForm,
        productLine: "book",
        vendor: selectedVendor,
        academicYear,
        organization,
        catalog: cat._id,
      };
      const saved = editingItem
        ? await axios.put(`/items/${editingItem._id}`, payload)
        : await axios.post("/items", payload);

      const otherItems = (cat.items || []).filter(
        (i) => (i.productLine || "book") !== "uniform"
      );
      const updatedItems = editingItem
        ? otherItems.map((i) =>
            i._id === saved.data._id ? saved.data : i
          )
        : [...otherItems, saved.data];

      await saveCatalogLinks({
        ...cat,
        items: updatedItems,
        collections: cat.collections || [],
      });
      setItemDialogOpen(false);
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save item", "error");
    }
  };

  const handleDeleteItem = async (item) => {
    if (
      !(await confirm({
        title: "Delete Item",
        message: "Are you sure you want to delete this item?",
      }))
    )
      return;
    try {
      await axios.delete(`/items/${item._id}`);
      const updatedItems = bookItems.filter((i) => i._id !== item._id);
      await saveCatalogLinks({
        ...catalog,
        items: updatedItems,
        collections: catalog.collections || [],
      });
      showMessage("Item removed from catalog", "success");
    } catch (err) {
      showMessage(
        err.response?.data?.msg || "Failed to delete item",
        "error"
      );
    }
  };

  const handleCollectionSave = async () => {
    if (!colForm.name.trim())
      return showMessage("Collection name required", "warning");

    try {
      const cat = catalog?._id ? catalog : await ensureCatalog();
      const payload = {
        ...colForm,
        vendor: selectedVendor,
        academicYear,
        organization,
        catalog: cat._id,
      };
      const saved = editingCol
        ? await axios.put(`/collections/${editingCol._id}`, payload)
        : await axios.post("/collections", payload);

      const updatedCollections = editingCol
        ? (cat.collections || []).map((c) =>
            c._id === saved.data._id ? saved.data : c
          )
        : [...(cat.collections || []), saved.data];

      await saveCatalogLinks({
        ...cat,
        items: bookItems,
        collections: updatedCollections,
      });
      setColDialogOpen(false);
    } catch (err) {
      showMessage(err.response?.data?.msg || "Failed to save collection", "error");
    }
  };

  const handleDeleteCollection = async (col) => {
    if (
      !(await confirm({
        title: "Delete Collection",
        message: "Are you sure you want to delete this collection?",
      }))
    )
      return;
    try {
      await axios.delete(`/collections/${col._id}`);
      const updatedCollections = (catalog.collections || []).filter(
        (c) => c._id !== col._id
      );
      await saveCatalogLinks({
        ...catalog,
        items: bookItems,
        collections: updatedCollections,
      });
      showMessage("Collection removed from catalog", "success");
    } catch (err) {
      showMessage(
        err.response?.data?.msg || "Failed to delete collection",
        "error"
      );
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Book Catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Manage textbooks and book sets for book suppliers only.
      </Typography>

      <FormControl sx={{ mb: 2, minWidth: 300 }}>
        <InputLabel>Book vendor</InputLabel>
        <Select
          value={selectedVendor}
          onChange={(e) => setSelectedVendor(e.target.value)}
          label="Book vendor"
        >
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>
              {v.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!vendors.length && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No book vendors found. Add a vendor with type &quot;Book supplier&quot; first.
        </Typography>
      )}

      {selectedVendor && (
        <>
          <Typography variant="h6" sx={{ my: 2 }}>
            Categories
          </Typography>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              value={catInput}
              onChange={(e) => setCatInput(e.target.value)}
              label="New Category"
            />
            <Button
              onClick={handleAddCategory}
              startIcon={<Add />}
              variant="contained"
              size="small"
            >
              Add
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2 }}>
            {(catalog?.categories || []).map((cat) => (
              <Chip
                key={cat}
                label={cat}
                onDelete={() => handleDeleteCategory(cat)}
              />
            ))}
          </Stack>

          <Box sx={{ mt: 3 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="h6">Books</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => openItemDialog()}
              >
                Add Book
              </Button>
            </Box>
            <CommonTable
              columns={[
                { label: "Name", field: "name" },
                { label: "Price", field: "price" },
                {
                  label: "Cost",
                  renderCell: (row) => {
                    if (row.costPrice != null)
                      return `${row.costPrice.toFixed(2)}`;
                    if (row.commissionPercent != null && row.price != null) {
                      return `${(
                        row.price *
                        (1 - row.commissionPercent / 100)
                      ).toFixed(2)}`;
                    }
                    return "";
                  },
                },
                {
                  label: "Grades",
                  renderCell: (i) => (i.grades || []).join(", "),
                },
                {
                  label: "Programs",
                  renderCell: (i) => (i.programs || []).join(", "),
                },
                {
                  label: "Actions",
                  renderCell: (item) => (
                    <>
                      <Button size="small" onClick={() => openItemDialog(item)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteItem(item)}
                      >
                        Delete
                      </Button>
                    </>
                  ),
                },
              ]}
              rows={bookItems}
            />
          </Box>

          <Box sx={{ mt: 3 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="h6">Collections (book sets)</Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setColForm(colDefaultForm);
                  setEditingCol(null);
                  setColDialogOpen(true);
                }}
              >
                Add Collection
              </Button>
            </Box>
            <CommonTable
              columns={[
                { label: "Name", field: "name" },
                { label: "Price", field: "price" },
                {
                  label: "Grades",
                  renderCell: (c) => (c.grades || []).join(", "),
                },
                {
                  label: "Programs",
                  renderCell: (c) => (c.programs || []).join(", "),
                },
                {
                  label: "Actions",
                  renderCell: (col) => (
                    <>
                      <Button
                        size="small"
                        onClick={() => {
                          setColForm({
                            ...col,
                            costMode:
                              col.costPrice != null
                                ? "costPrice"
                                : "commissionPercent",
                          });
                          setEditingCol(col);
                          setColDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteCollection(col)}
                      >
                        Delete
                      </Button>
                    </>
                  ),
                },
              ]}
              rows={catalog?.collections || []}
            />
          </Box>
        </>
      )}

      <ItemDialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        onSave={handleItemSave}
        form={itemForm}
        setForm={setItemForm}
        masters={masters}
        categories={catalog?.categories || []}
        isEditing={!!editingItem}
        lockProductLine
      />

      <CollectionDialog
        open={colDialogOpen}
        onClose={() => setColDialogOpen(false)}
        onSave={handleCollectionSave}
        form={colForm}
        setForm={setColForm}
        masters={masters}
        categories={catalog?.categories || []}
        items={bookItems}
        isEditing={!!editingCol}
      />
    </Box>
  );
};

export default BookCatalog;
